// v16.6 — Avatar gate context.
//
// Pattern song song với `RatingGateContext`. Mục đích: nếu VĐV vẫn dùng avatar
// mặc định (chưa upload ảnh thật), chặn navigate sang trang đăng ký giải và
// mở luôn `UpdateProfileModal` để user upload ngay.
//
// User choices v16.6:
//   1c — Avatar mặc định = null/empty OR dicebear/ui-avatars placeholder.
//   2c — Chặn ở cả nút "Đăng ký" (context này) + page-load `/register`
//        (dùng `AvatarRequiredGate` inline).
//   3a — Mở luôn `UpdateProfileModal`; sau khi user upload avatar mới → tự
//        đóng và tiếp tục navigate.
//   4a — BE cũng chặn (400 `avatar_required`).
//
// Usage:
//   const { requireCustomAvatar } = useAvatarGate();
//   requireCustomAvatar(() => navigate("/.../register"));
import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { hasNoCustomAvatar } from "../lib/avatarHelpers";

// v17.11 Lazy Load Đợt 2 — Modal upload avatar chỉ mount khi cần → tách
// ra chunk riêng, không kéo vào initial bundle. Đa số user KHÔNG mở modal
// này trong session (chỉ khi đăng ký giải và chưa có avatar).
const UpdateProfileModal = lazy(() =>
  import(
    /* webpackChunkName: "modal-update-profile" */
    "../components/athlete/UpdateProfileModal"
  ),
);

// v20.1 — Blocker cho user MỚI (must_upload_avatar=true). Chặn TOÀN BỘ app
// đến khi upload avatar thật (BE trả 403 avatar_required cho mọi API khác).
const AvatarRequiredBlocker = lazy(() =>
  import(
    /* webpackChunkName: "avatar-required-blocker" */
    "../components/common/AvatarRequiredBlocker"
  ),
);

const AvatarGateContext = createContext({
  requireCustomAvatar: (fn) => fn?.(),
});

export function AvatarGateProvider({ children }) {
  const { user, refreshMe } = useAuth();
  const [open, setOpen] = useState(false);
  const pendingActionRef = useRef(null);

  const requireCustomAvatar = useCallback(
    (action, opts = {}) => {
      // Guest → để GuestLoginGate xử lý ở tầng ngoài.
      if (!user?.id) {
        return action?.();
      }
      // Có avatar thật rồi → cho qua ngay.
      if (!hasNoCustomAvatar(user)) {
        return action?.();
      }
      // Chưa upload → mở UpdateProfileModal + lưu action để resume.
      pendingActionRef.current = action || null;
      toast.warning(opts.message || "Bạn cần cập nhật ảnh đại diện thật trước khi đăng ký giải.", {
        description: opts.description || "BTC cần ảnh thật để xếp danh sách và đối chiếu tại giải.",
        duration: 4000,
      });
      setOpen(true);
      return undefined;
    },
    [user?.id, user?.avatar_url],
  );

  const onClose = () => {
    setOpen(false);
    pendingActionRef.current = null;
  };

  const onUpdated = async (updated) => {
    // updated: user object từ BE trả về sau POST /auth/me/avatar hoặc PATCH /auth/me.
    // Refresh AuthContext để `hasNoCustomAvatar(user)` sync đúng ở lần render tiếp.
    try { await refreshMe?.(); } catch { /* silent */ }
    // Chỉ resume action nếu user THỰC SỰ đã có avatar mới (đề phòng user
    // save profile mà không upload avatar → gate vẫn phải chặn).
    if (updated && !hasNoCustomAvatar(updated)) {
      setOpen(false);
      const next = pendingActionRef.current;
      pendingActionRef.current = null;
      if (next) {
        setTimeout(() => { try { next(); } catch { /* swallow */ } }, 250);
      }
    }
  };

  const value = useMemo(() => ({ requireCustomAvatar }), [requireCustomAvatar]);

  // v20.1 — Gate app-wide cho user MỚI. Chỉ kích hoạt khi BE set flag này
  // (register-with-otp / Google / Apple sau v20.1). User cũ luôn = false → không
  // ảnh hưởng. Đặt logic tại đây (thay vì App.js) để tận dụng sẵn `useAuth`
  // + tách chunk lazy load.
  const forceAvatarUpload = Boolean(user?.id && user?.must_upload_avatar === true);

  return (
    <AvatarGateContext.Provider value={value}>
      {children}
      {open && (
        <Suspense fallback={null}>
          <UpdateProfileModal
            open={open}
            user={user}
            onClose={onClose}
            onUpdated={onUpdated}
          />
        </Suspense>
      )}
      {forceAvatarUpload && (
        <Suspense fallback={null}>
          <AvatarRequiredBlocker />
        </Suspense>
      )}
    </AvatarGateContext.Provider>
  );
}

export function useAvatarGate() {
  return useContext(AvatarGateContext);
}
